// src/pages/CustomerAccount/pages/Components/DashboardContent.jsx
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { User, Info, Save, Truck, Eye, ChevronLeft, ChevronRight, Phone, Edit2, X, Loader2 } from 'lucide-react';
import { authAxiosInstance } from '../../../../utils/axiosinstance';
import { COLORS } from './CustomerAccount.styles';
import OrdersTable from './OrdersTable';
import EditModal from './EditModal';
import { 
  requestPhoneChangeThunk, 
  verifyPhoneChangeThunk 
} from '../../../../redux/features/Auth/authThunk';
import { resetPhoneChange } from '../../../../redux/features/Auth/authSlice';

const DashboardContent = ({
  userInfo,
  recentOrders,
  discountedProducts,
  loadingProducts,
  onEdit,
  isEditing,
  editData,
  onSave,
  onCancel,
  onViewAllOrders,
  navigate,
  successMessage,
  loadingOrders,
}) => {
  const dispatch = useDispatch();
  const { userInfo: userInfoFromRedux } = useSelector((state) => state.auth) || {};
  const { phoneChangeStep, newPhone, loading, error } = useSelector((state) => state.auth) || {};
  
  const [slideIndex, setSlideIndex] = useState(0);
  const itemsPerPage = 3;

  // ===== State برای مودال تغییر شماره تلفن =====
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneForm, setPhoneForm] = useState({
    newPhone: '',
    otpCode: '',
  });
  const [phoneFormError, setPhoneFormError] = useState('');

  const totalSlides = Math.max(1, Math.ceil((discountedProducts || []).length / itemsPerPage));
  const nextSlide = () => setSlideIndex((prev) => (prev + 1) % totalSlides);
  const prevSlide = () => setSlideIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  const getCurrentProducts = () => (discountedProducts || []).slice(slideIndex * itemsPerPage, (slideIndex * itemsPerPage) + itemsPerPage);

  // ===== فرم اطلاعات کاربری =====
  const [profileForm, setProfileForm] = useState({
    fullName: `${userInfo.firstName} ${userInfo.lastName}`.trim(),
    phone: userInfo.phone || '',
  });
  const [localSuccess, setLocalSuccess] = useState('');

  // ===== به‌روزرسانی نام =====
  const handleProfileUpdate = async () => {
    if (!profileForm.fullName.trim()) {
      alert('لطفاً نام خود را وارد کنید.');
      return;
    }

    try {
      const authBackend = authAxiosInstance(userInfoFromRedux, dispatch);
      const response = await authBackend.put('/api/v1/users/profile/update/', {
        name: profileForm.fullName,
      });
      
      const updatedUserInfo = { ...userInfoFromRedux, ...response.data };
      localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
      
      setLocalSuccess('✅ اطلاعات با موفقیت به‌روز شد!');
      setTimeout(() => setLocalSuccess(''), 3000);
    } catch (err) {
      alert('خطا در به‌روزرسانی: ' + (err?.response?.data?.detail || err.message));
    }
  };

  // ============================================================
  // 📱 تغییر شماره تلفن
  // ============================================================

  // ===== باز کردن مودال (با ریست state) =====
  const handleOpenPhoneModal = () => {
    dispatch(resetPhoneChange());
    setPhoneForm({ newPhone: '', otpCode: '' });
    setPhoneFormError('');
    setShowPhoneModal(true);
  };

  // ===== بستن مودال =====
  const closePhoneModal = () => {
    setShowPhoneModal(false);
    dispatch(resetPhoneChange());
    setPhoneForm({ newPhone: '', otpCode: '' });
    setPhoneFormError('');
  };

  // ===== مرحله 1: درخواست کد تایید برای شماره جدید =====
  const handleRequestPhoneChange = async () => {
    if (!phoneForm.newPhone || phoneForm.newPhone.length !== 11 || !/^\d+$/.test(phoneForm.newPhone)) {
      setPhoneFormError('شماره تلفن جدید باید ۱۱ رقم باشد.');
      return;
    }
    if (phoneForm.newPhone === userInfo.phone) {
      setPhoneFormError('شماره تلفن جدید با شماره فعلی یکسان است.');
      return;
    }
    setPhoneFormError('');
    
    try {
      await dispatch(requestPhoneChangeThunk(phoneForm.newPhone)).unwrap();
    } catch (err) {
      const msg = err?.detail || err?.message || 'خطا در ارسال کد تایید';
      setPhoneFormError(msg);
    }
  };

  // ===== مرحله 2: تایید کد و تغییر شماره (ورودی از راست به چپ) =====
  const handleVerifyPhoneChange = async () => {
    if (!phoneForm.otpCode || phoneForm.otpCode.length !== 6) {
      setPhoneFormError('کد تایید ۶ رقمی را کامل وارد کنید.');
      return;
    }
    setPhoneFormError('');
    
    try {
      await dispatch(verifyPhoneChangeThunk(phoneForm.otpCode)).unwrap();
      setLocalSuccess('✅ شماره تلفن با موفقیت تغییر کرد!');
      setTimeout(() => setLocalSuccess(''), 3000);
      setTimeout(() => {
        setShowPhoneModal(false);
        dispatch(resetPhoneChange());
        setPhoneForm({ newPhone: '', otpCode: '' });
      }, 1500);
    } catch (err) {
      const msg = err?.detail || err?.message || 'کد نامعتبر است';
      setPhoneFormError(msg);
    }
  };

  const handleViewDetails = (order) => {
    if (order._id) {
      navigate(`/order/${order._id}`);
    } else {
      onViewAllOrders();
    }
  };

  const currentProducts = getCurrentProducts();

  return (
    <div className="space-y-4 sm:space-y-5">
      {(successMessage || localSuccess) && (
        <div className="rounded-xl bg-green-50 p-3 text-center text-sm font-medium text-green-700 border border-green-200">
          {successMessage || localSuccess}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3" style={{ borderColor: COLORS.border }}>
        <h2 className="flex items-center gap-2 text-base font-bold sm:text-xl" style={{ color: COLORS.text }}>
          <Info className="h-5 w-5 shrink-0" style={{ color: COLORS.primary }} />
          اطلاعات بیشتر
        </h2>
        <span className="text-xs" style={{ color: COLORS.primaryLight }}>{userInfo.memberSince}</span>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/* ===== بخش تخفیف‌های ویژه ===== */}
        <div className="w-full overflow-hidden rounded-xl border p-3 sm:p-4" style={{ background: COLORS.cardBg, borderColor: COLORS.border }}>
          <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
            <h3 className="flex min-w-0 items-center gap-2 text-sm font-semibold" style={{ color: COLORS.text }}>
              <span className="truncate">تخفیف‌های ویژه</span>
            </h3>
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
              <button onClick={prevSlide} disabled={totalSlides <= 1} className="inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-[#f0f3ec] disabled:opacity-30" style={{ color: COLORS.primary }}>
                <ChevronRight className="h-5 w-5" />
              </button>
              <span className="min-w-[2.5rem] text-center text-xs" style={{ color: COLORS.primaryLight }}>{slideIndex + 1} / {totalSlides}</span>
              <button onClick={nextSlide} disabled={totalSlides <= 1} className="inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-[#f0f3ec] disabled:opacity-30" style={{ color: COLORS.primary }}>
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
          </div>
          {loadingProducts ? (
            <div className="text-center py-8 text-sm" style={{ color: COLORS.primaryLight }}>در حال بارگذاری محصولات...</div>
          ) : currentProducts.length > 0 ? (
            <div className="w-full space-y-2">
              {currentProducts.map(product => {
                const discountPrice = product.price * (1 - product.discountPercentage / 100);
                return (
                  <div key={product._id} className="flex w-full items-center gap-2 rounded-lg border bg-white p-2 transition-shadow hover:shadow-sm sm:gap-3" style={{ borderColor: COLORS.border }}>
                    <div className="relative shrink-0">
                      <img src={product.image || 'https://via.placeholder.com/64'} alt={product.name} className="h-14 w-14 rounded-lg object-cover sm:h-16 sm:w-16" />
                      {product.discountPercentage > 0 && (
                        <span className="absolute -right-1 -top-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, ${COLORS.warm})` }}>
                          {product.discountPercentage}%
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="truncate text-xs font-medium" style={{ color: COLORS.text }}>{product.name}</h4>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="text-[10px] line-through" style={{ color: COLORS.primaryLight }}>{product.price.toLocaleString()} تومان</span>
                        <span className="text-xs font-bold" style={{ color: COLORS.danger }}>{Math.round(discountPrice).toLocaleString()} تومان</span>
                      </div>
                    </div>
                    <button onClick={() => navigate(`/product/${product._id}`)} className="flex shrink-0 items-center gap-0.5 rounded-lg px-2 py-1.5 text-[10px] font-medium text-white transition-all hover:shadow-md sm:px-2.5" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                      <Eye className="h-3 w-3" />
                      <span className="hidden xs:inline sm:inline">جزئیات</span>
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-sm" style={{ color: COLORS.primaryLight }}>
              محصول تخفیف‌داری یافت نشد.
            </div>
          )}
        </div>

        {/* ===== بخش اطلاعات کاربری ===== */}
        <div className="h-full w-full rounded-xl border p-3 sm:p-4" style={{ background: `linear-gradient(135deg, ${COLORS.primaryBg}, white)`, borderColor: COLORS.border }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold" style={{ color: COLORS.text }}>
              <User className="h-4 w-4 shrink-0" style={{ color: COLORS.primary }} />
              اطلاعات کاربری
            </h3>
            <button
              onClick={handleOpenPhoneModal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
              style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}
            >
              <Edit2 className="w-3.5 h-3.5" />
              تغییر شماره
            </button>
          </div>
          <div className="w-full space-y-3">
            {/* فیلد نام */}
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: COLORS.primaryLight }}>
                نام و نام خانوادگی
              </label>
              <input
                type="text"
                value={profileForm.fullName}
                onChange={e => setProfileForm({ ...profileForm, fullName: e.target.value })}
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:ring-2 transition-all"
                style={{ borderColor: COLORS.border, '--tw-ring-color': `${COLORS.primary}55`, backgroundColor: 'white', color: COLORS.text }}
                placeholder="نام خود را وارد کنید"
              />
            </div>

            {/* فیلد شماره تلفن (فقط نمایشی) */}
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: COLORS.primaryLight }}>
                شماره تلفن همراه
              </label>
              <div className="relative">
                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  value={profileForm.phone}
                  disabled
                  className="w-full rounded-lg border bg-gray-50 px-3 py-2.5 pr-10 text-sm text-gray-500 cursor-not-allowed"
                  style={{ borderColor: COLORS.border }}
                />
              </div>
              <p className="mt-0.5 text-[10px]" style={{ color: COLORS.primaryLight }}>
                برای تغییر شماره، روی دکمه "تغییر شماره" کلیک کنید
              </p>
            </div>

            {/* دکمه ذخیره نام */}
            <button
              onClick={handleProfileUpdate}
              className="mt-2 w-full rounded-lg bg-gradient-to-r py-2.5 text-xs font-medium text-white shadow-sm transition-all hover:shadow-md active:scale-[0.98]"
              style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}
            >
              <span className="flex items-center justify-center gap-1.5">
                <Save className="h-3.5 w-3.5" />
                به‌روزرسانی نام
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ===== سفارشات اخیر ===== */}
      <div className="rounded-xl border p-3 sm:p-4" style={{ background: COLORS.cardBg, borderColor: COLORS.border }}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold" style={{ color: COLORS.text }}>
            <Truck className="h-4 w-4 shrink-0" style={{ color: COLORS.primaryLight }} />
            سفارشات اخیر
          </h3>
          <button onClick={onViewAllOrders} className="text-xs font-medium hover:underline" style={{ color: COLORS.primary }}>
            مشاهده همه
          </button>
        </div>
        {loadingOrders ? (
          <div className="text-center py-4 text-sm" style={{ color: COLORS.primaryLight }}>در حال بارگذاری...</div>
        ) : (
          <OrdersTable orders={recentOrders} onViewDetails={handleViewDetails} />
        )}
      </div>

      {/* ============================================================
          📱 مودال تغییر شماره تلفن
          ============================================================ */}
      {showPhoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" dir="rtl">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 relative">
            {/* دکمه بستن */}
            <button
              onClick={closePhoneModal}
              className="absolute top-4 left-4 p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#F0F4EB] flex items-center justify-center mx-auto mb-3">
                <Phone className="w-8 h-8 text-[#8A9A7B]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">
                {phoneChangeStep === 'idle' ? 'تغییر شماره تلفن' : 'تایید کد'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {phoneChangeStep === 'idle' 
                  ? 'شماره تلفن جدید را وارد کنید' 
                  : `کد ارسال شده به ${userInfo.phone} را وارد کنید`}
              </p>
            </div>

            {phoneFormError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                {phoneFormError}
              </div>
            )}

            {loading && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-center gap-2 text-blue-600 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                در حال پردازش...
              </div>
            )}

            {phoneChangeStep === 'idle' ? (
              // ===== مرحله 1: وارد کردن شماره جدید =====
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    شماره تلفن جدید <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phoneForm.newPhone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 11) {
                        setPhoneForm({ ...phoneForm, newPhone: value });
                        setPhoneFormError('');
                      }
                    }}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#8A9A7B] outline-none transition-all"
                    placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                    maxLength={11}
                    disabled={loading}
                  />
                  <p className="mt-1 text-xs text-gray-400">شماره تلفن باید ۱۱ رقم باشد</p>
                </div>

                <button
                  onClick={handleRequestPhoneChange}
                  disabled={loading}
                  className="w-full py-3.5 bg-[#8A9A7B] text-white rounded-xl font-bold hover:bg-[#6A7A5B] transition-all disabled:opacity-50 shadow-md"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      در حال ارسال...
                    </span>
                  ) : (
                    'ارسال کد تایید'
                  )}
                </button>

                <p className="text-center text-xs text-gray-400">
                  کد تایید به شماره فعلی شما ارسال خواهد شد
                </p>
              </div>
            ) : (
              // ===== مرحله 2: وارد کردن کد تایید (راست → چرب) =====
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    کد تایید <span className="text-red-500">*</span>
                  </label>

                  {/* ✅ شش کادر با جهت راست‌چین و فوکوس روی راست‌ترین */}
                  <div 
                    className="flex justify-center gap-2" 
                    style={{ display: 'flex', flexDirection: 'row-reverse' }}
                  >
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={phoneForm.otpCode[index] || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 1) {
                            const newCode = [...phoneForm.otpCode.split('')];
                            newCode[index] = value;
                            setPhoneForm({ ...phoneForm, otpCode: newCode.join('') });
                            setPhoneFormError('');
                            // حرکت به خانه بعدی (سمت چپ)
                            if (value && index < 5) {
                              const nextInput = document.querySelector(`input[name="otp-${index + 1}"]`);
                              if (nextInput) nextInput.focus();
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          // Backspace: به خانه قبلی (سمت راست)
                          if (e.key === 'Backspace' && !phoneForm.otpCode[index] && index > 0) {
                            const prevInput = document.querySelector(`input[name="otp-${index - 1}"]`);
                            if (prevInput) prevInput.focus();
                          }
                        }}
                        name={`otp-${index}`}
                        className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#8A9A7B] outline-none transition-all"
                        disabled={loading}
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>

                  <p className="mt-2 text-center text-xs text-gray-400">
                    کد ۶ رقمی را از راست به چپ وارد کنید
                  </p>
                </div>

                <button
                  onClick={handleVerifyPhoneChange}
                  disabled={loading}
                  className="w-full py-3.5 bg-[#8A9A7B] text-white rounded-xl font-bold hover:bg-[#6A7A5B] transition-all disabled:opacity-50 shadow-md"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      در حال تایید...
                    </span>
                  ) : (
                    'تایید و تغییر شماره'
                  )}
                </button>

                <button
                  onClick={() => {
                    dispatch(resetPhoneChange());
                    setPhoneForm({ ...phoneForm, otpCode: '' });
                    setPhoneFormError('');
                  }}
                  className="w-full text-sm text-[#8A9A7B] hover:underline"
                  disabled={loading}
                >
                  بازگشت و ویرایش شماره جدید
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {isEditing && (
        <EditModal
          userInfo={editData || userInfo}
          onSave={onSave}
          onCancel={onCancel}
          section={editData?.section}
        />
      )}
    </div>
  );
};

export default DashboardContent;